import {
    BidItemChangeViewModel,
    BidListItemModel,
    BidPointEditModel,
    BidPointViewModel,
    CargoModel,
    CarNumberModel,
    DistributionBidListModel,
    DriverUserSimpleModel,
    ExpressBidSettingsJsonObject,
    SegmentItemModel,
    TrailerNumberModel,
} from "api";
import cn from "classnames";
import { CAN_RESET_DISTRIBUTION_BID_IN_PROCESS_STATUS_KEY } from "constants/localStorage";
import { withoutStartPoint } from "helpers/bidpoint";
import { dateTimeToView, plainDate, timeOffset } from "helpers/date";
import {
    bidItemTypesEnum,
    bidStatuses,
    bidStatusesEnum,
    counterpartyStatuses,
    counterpartyStatusesEnum,
    distributionBidStatuses,
    distributionBidStatusesEnum,
    featureModuleTypesEnum,
    mapObjectTypesEnum,
    organizationFlags,
    organizationFlagsEnum,
    simplifiedTypeCodesEnum,
} from "helpers/enums";
import { ending } from "helpers/i18n";
import { isImpersonatingUser } from "helpers/localStorage";
import { paths } from "helpers/paths";
import { arraysEqual, enumLabel } from "helpers/system";
import uniq from "lodash/uniq";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ModalBodyProps } from "reducers/modal";
import { closeBidsList, getBidCollisions } from "sources/books/bids";
import { resetInProcessStatus } from "sources/books/distributionBids";

const compoundCargosFlagVal = organizationFlags.find(
    (item) => item.value === organizationFlagsEnum.HasCompoundCargos,
)?.intValue;

const bidPointAddress = (point?: { geozone?: { address?: string; city?: string; village?: string } }) =>
    point?.geozone?.address || point?.geozone?.city || point?.geozone?.village;

const routeLabel = (firstAddress?: string, lastAddress?: string) =>
    [firstAddress, lastAddress].filter(Boolean).join(" → ");

/** Builds an informative label for a bid selector used by electronic shipping documents. */
export const bidSelectLabel = (bid: BidListItemModel) => {
    const route = routeLabel(bidPointAddress(bid.firstBidPoint), bidPointAddress(bid.lastBidPoint));
    return [
        `Заявка №${bid.id}${bid.externalId ? ` (внеш. №${bid.externalId})` : ""}`,
        enumLabel(bidStatuses, bid.status),
        dateTimeToView(bid.createdAt),
        route,
        bid.car?.number && `ТС ${bid.car.number}`,
        bid.driverName && `водитель ${bid.driverName}`,
    ]
        .filter(Boolean)
        .join(" · ");
};

/** Builds an informative label for a distribution bid selector used by electronic shipping documents. */
export const distributionBidSelectLabel = (distributionBid: DistributionBidListModel) => {
    const route = routeLabel(
        distributionBid.pointAddresses?.[0] || bidPointAddress(distributionBid.firstBidPoint),
        distributionBid.pointAddresses?.at(-1) || bidPointAddress(distributionBid.lastBidPoint),
    );
    return [
        `Заказ №${distributionBid.id}${distributionBid.externalId ? ` (внеш. №${distributionBid.externalId})` : ""}`,
        enumLabel(distributionBidStatuses, distributionBid.status),
        dateTimeToView(distributionBid.createdAt),
        distributionBid.counterparty?.name,
        route,
        distributionBid.bidId && `заявка №${distributionBid.bidId}`,
    ]
        .filter(Boolean)
        .join(" · ");
};

export const counterpartyStatusBadge = (status: keyof typeof counterpartyStatusesEnum, showAllStatuses = false) => {
    if (status === counterpartyStatusesEnum.None && !showAllStatuses) {
        return null;
    }
    const classNames = cn("badge", {
        "badge-success": status === counterpartyStatusesEnum.Priority,
        "badge-secondary": status === counterpartyStatusesEnum.Suspended,
        "badge-light": status === counterpartyStatusesEnum.None,
    });
    return <small className={classNames}>{enumLabel(counterpartyStatuses, status)}</small>;
};

export const counterpartyInsolvency = (data: {
    inn: string;
    insolvencyRank: number;
    ratings: Array<{ organizationName: string; comment: string }>;
}) => {
    if (!data) {
        return null;
    }
    return (
        <div className="font-size-12">
            <span className="text-danger">
                ⚠️ Контрагент отмечен как неплатежеспособный у {data.insolvencyRank} компани
                {ending(data.insolvencyRank, "и", "й", "й")} в CARGO.RUN
            </span>
            {data.ratings.length > 0 && (
                <div>
                    Комментарии:
                    <ol className="pl-3 mb-0">
                        {data.ratings.map((rating, i) => (
                            <li key={i}>
                                <b>{rating.organizationName}</b>
                                <br />
                                {rating.comment}
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
};
export const counterpartyInsolvencyTooltip = (
    insolvencyRegistryEntry: { insolvencyRank: number; ratings: Array<{ organizationName: string; comment: string }> },
    modal: (params: {}) => void,
) => {
    const tooltipComments = insolvencyRegistryEntry.ratings?.slice(0, 5);
    const modalComments = insolvencyRegistryEntry.ratings?.slice(5, insolvencyRegistryEntry.ratings.length);
    const overlayContent = () => (
        <div className="mw-350">
            ⚠️ Контрагент отмечен как неплатежеспособный у {insolvencyRegistryEntry.insolvencyRank} компани
            {ending(insolvencyRegistryEntry.insolvencyRank, "и", "й", "й")} в CARGO.RUN
            {tooltipComments?.length > 0 && (
                <ol className="pl-3 mb-0">
                    {tooltipComments.map((rating, i) => (
                        <li key={i}>
                            <b>{rating.organizationName}</b>
                            <br />
                            {rating.comment}
                        </li>
                    ))}
                </ol>
            )}
            {modalComments?.length > 0 && (
                <div className="pt-3 font-weight-bold">
                    Нажмите на статус, для того чтобы увидеть остальные комментарии контрагента
                </div>
            )}
        </div>
    );
    const showModal = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        if (modalComments.length === 0) {
            return;
        }
        modal({
            name: "infoModal",
            windowMod: "wide",
            title: "Комментарии относительно надежности контрагента",
            body: (
                <div className="modal__body">
                    <ol className="pl-3 mb-0">
                        {insolvencyRegistryEntry.ratings.map((rating, i) => (
                            <li key={i}>
                                <b>{rating.organizationName}</b>
                                <br />
                                {rating.comment}
                            </li>
                        ))}
                    </ol>
                </div>
            ),
        });
    };
    return (
        <Tooltip overlay={overlayContent()} placement="right">
            <span className="badge badge-danger" onClick={showModal}>
                Неплатежеспособный
            </span>
        </Tooltip>
    );
};

export const getStatusByFields = (
    {
        id,
        bidId,
        hiredCarCompletedAt,
        status,
        isDeleted,
    }: {
        id: number | string;
        bidId?: number;
        hiredCarCompletedAt?: string;
        status: keyof typeof distributionBidStatusesEnum;
        isDeleted: boolean;
    },
    {
        modal,
        onInProcessReset,
    }: {
        modal?: (params: ModalBodyProps) => void;
        onInProcessReset?: () => void;
    } = {},
) => {
    if (bidId) {
        return <span className="badge badge-primary">Создана заявка</span>;
    } else if (status === distributionBidStatusesEnum.InProcess) {
        const canResetInProcessStatus =
            !!modal &&
            isImpersonatingUser() &&
            localStorage.getItem(CAN_RESET_DISTRIBUTION_BID_IN_PROCESS_STATUS_KEY) === "true";
        if (canResetInProcessStatus) {
            const resetStatus = (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                modal({
                    name: "confirm",
                    title: "Сброс заполнения заказа",
                    text: 'Вы уверены, что хотите сбросить статус "Заполняется" у заказа?',
                    buttons: { confirm: "Да", cancel: "Нет" },
                    action: async () => {
                        const result = await resetInProcessStatus(id);
                        if (result !== false && result !== undefined) {
                            await onInProcessReset?.();
                        }
                        return result;
                    },
                });
            };
            return (
                <Tooltip overlay="Нажмите на статус заказа, чтобы сбросить его заполнение." placement="right">
                    <button type="button" className="badge badge-success border-0 cursor-pointer" onClick={resetStatus}>
                        {enumLabel(distributionBidStatuses, distributionBidStatusesEnum.InProcess)}
                    </button>
                </Tooltip>
            );
        }
        return (
            <span className="badge badge-success">
                {enumLabel(distributionBidStatuses, distributionBidStatusesEnum.InProcess)}
            </span>
        );
    } else if (status === distributionBidStatusesEnum.Canceled) {
        return (
            <span className="badge badge-secondary">
                {enumLabel(distributionBidStatuses, distributionBidStatusesEnum.Canceled)}
            </span>
        );
    } else if (isDeleted) {
        return <span className="badge badge-dark">Удален</span>;
    } else if (hiredCarCompletedAt) {
        return <span className="badge badge-success">Выполнен</span>;
    } else {
        return <span className="badge badge-secondary">Без заявки</span>;
    }
};

export const formatExtendedProp = (value: string, typeCode: keyof typeof simplifiedTypeCodesEnum) => {
    switch (typeCode) {
        case simplifiedTypeCodesEnum.Boolean:
            return value === "true" ? "Да" : "Нет";
        case simplifiedTypeCodesEnum.DateTime:
        case simplifiedTypeCodesEnum.DateTimeWithoutTimezone:
            return value ? dateTimeToView(value) : "";
        default:
            return value;
    }
};
/** Confirms compound creation or reports empty-mileage collisions when editing bid dates. */
export const checkCompoundBids = (props: {
    intFlags: number;
    action: () => void;
    firstPoint: BidPointEditModel;
    lastPoint: BidPointEditModel;
    carOption: { carId: number; number: string };
    trailerOption?: { number?: string } | null;
    id: number;
    isEmptyMileageBid: boolean;
    modal: (props: ModalBodyProps) => void;
    setLoading: (value: boolean) => void;
}) => {
    const hasCompoundCargos = (props.intFlags & compoundCargosFlagVal!) === compoundCargosFlagVal;
    if (!hasCompoundCargos) {
        return props.action();
    }
    const { firstPoint, lastPoint, carOption, trailerOption, id, isEmptyMileageBid } = props;
    return checkCompoundCondition({ firstPoint, lastPoint, id, carId: carOption.carId, isEmptyMileageBid }).then(
        ({ hasCompoundBids, collisionEmptyMileageBidId, trailerCollisionEmptyMileageBidId }) => {
            if (hasCompoundBids) {
                return notificationModal({
                    modal: props.modal,
                    setLoading: props.setLoading,
                    action: props.action,
                    title: "Изменение заявки",
                    text: (
                        <>
                            Внимание! Данная заявка станет сборной,
                            <br />
                            так как у машины <strong>{carOption.number}</strong> есть активная заявка на этот период
                        </>
                    ),
                    buttons: {
                        confirm: "Сохранить заявку",
                        cancel: "Изменить даты заявки",
                    },
                });
            } else if (collisionEmptyMileageBidId || trailerCollisionEmptyMileageBidId) {
                return props.modal({
                    name: "default",
                    title: "Запуск заявки в работу",
                    onCloseAction: () => props.setLoading(false),
                    body: emptyMileageCollisionText({
                        carOption,
                        trailerOption,
                        collisionEmptyMileageBidId,
                        trailerCollisionEmptyMileageBidId,
                    }),
                });
            } else {
                return props.action();
            }
        },
    );
};

/** Checks planning, compound creation, and empty-mileage collisions before starting a bid. */
export const checkActiveBids = (props: {
    intFlags: number;
    action: () => void;
    firstPoint: BidPointEditModel;
    lastPoint: BidPointEditModel;
    carOption: { carId: number; number: string };
    trailerOption?: { number?: string } | null;
    id: number;
    workRestInfo: ExpressBidSettingsJsonObject;
    isEmptyMileageBid: boolean;
    modal: (props: ModalBodyProps) => void;
    setLoading: (value: boolean) => void;
    availableFeatureModules: (keyof typeof featureModuleTypesEnum)[];
}) => {
    const {
        firstPoint,
        lastPoint,
        carOption,
        trailerOption,
        id,
        isEmptyMileageBid,
        intFlags,
        availableFeatureModules,
    } = props;
    const hasCompoundCargos = (intFlags & compoundCargosFlagVal!) === compoundCargosFlagVal;
    const checkCompoundReq =
        (hasCompoundCargos && isEmptyMileageBid === false) ||
        availableFeatureModules.includes(featureModuleTypesEnum.Exploitation as keyof typeof featureModuleTypesEnum)
            ? checkCompoundCondition({ firstPoint, lastPoint, id, carId: carOption.carId, isEmptyMileageBid })
            : Promise.resolve(false);
    return Promise.all([checkPlanningCondition(carOption.carId), checkCompoundReq]).then(([activeBid, compoudRes]) => {
        let {
            hasCompoundBids,
            collisionEmptyMileageBidId,
            trailerCollisionEmptyMileageBidId,
            approvingServiceWorkLogistTaskIds,
        }: {
            hasCompoundBids?: boolean;
            collisionEmptyMileageBidId?: number | null;
            trailerCollisionEmptyMileageBidId?: number | null;
            approvingServiceWorkLogistTaskIds?: number[] | null;
        } = typeof compoudRes === "boolean" ? {} : compoudRes;
        if (!(hasCompoundCargos && isEmptyMileageBid === false)) {
            hasCompoundBids = false;
            collisionEmptyMileageBidId = undefined;
            trailerCollisionEmptyMileageBidId = undefined;
        }
        const plannedBidLabel = bidStatuses.find((item) => item.value === bidStatusesEnum.Planned)!.label;
        const approvingServiceWorkText = () => {
            if (!approvingServiceWorkLogistTaskIds || approvingServiceWorkLogistTaskIds.length === 0) {
                return "";
            } else {
                return (
                    <div className="pt-2">
                        Период заявки пересекается с задачей на согласование выполнения сервисной работы.
                        <br />
                        Проверьте актуальность задачи перед назначением новой заявки.
                        {approvingServiceWorkLogistTaskIds.map((taskId) => (
                            <div key={taskId}>
                                <a href={paths.tasksShow(taskId)} rel="noopener noreferrer" target="_blank">
                                    Задача {taskId}
                                </a>
                            </div>
                        ))}
                    </div>
                );
            }
        };
        if (collisionEmptyMileageBidId || trailerCollisionEmptyMileageBidId) {
            return props.modal({
                name: "default",
                title: "Запуск заявки в работу",
                onCloseAction: () => props.setLoading(false),
                CustomBody: () => (
                    <div className="small">
                        {emptyMileageCollisionText({
                            carOption,
                            trailerOption,
                            collisionEmptyMileageBidId,
                            trailerCollisionEmptyMileageBidId,
                        })}
                        {approvingServiceWorkText()}
                    </div>
                ),
            });
        }
        if (activeBid && hasCompoundBids) {
            return notificationModal({
                modal: props.modal,
                setLoading: props.setLoading,
                action: props.action,
                text: (
                    <>
                        Внимание! Будет создана сборная заявка со статусом &quot;<strong>{plannedBidLabel}</strong>
                        &quot;,
                        <br />
                        так как у машины <strong>{carOption.number}</strong> на этот период есть активная и текущая
                        незакрытая заявки
                        <br />
                        Пока не будет закрыта заявка&nbsp;
                        <Link to={paths.bidShow("bid", activeBid.id)}>{activeBid.id}</Link>, водитель не получит
                        уведомления по новой заявке.
                        {approvingServiceWorkText()}
                    </>
                ),
            });
        }
        if (activeBid) {
            return notificationModal({
                modal: props.modal,
                setLoading: props.setLoading,
                action: props.action,
                text: (
                    <>
                        Внимание! Данная заявка получит статус &quot;<strong>{plannedBidLabel}</strong>&quot;,
                        <br />
                        так как у машины <strong>{carOption.number}</strong> есть незакрытая заявка&nbsp;
                        <Link to={paths.bidShow("bid", activeBid.id)}>{activeBid.id}</Link>
                        .
                        <br />
                        Пока она не будет закрыта, водитель не получит уведомления по новой заявке.
                        {approvingServiceWorkText()}
                    </>
                ),
            });
        }
        if (hasCompoundBids) {
            return notificationModal({
                modal: props.modal,
                setLoading: props.setLoading,
                action: props.action,
                text: (
                    <>
                        Внимание! Будет создана сборная заявка,
                        <br />
                        так как у машины <strong>{carOption.number}</strong> есть активная заявка на этот период
                        {approvingServiceWorkText()}
                    </>
                ),
                buttons: {
                    confirm: "Добавить cборную заявку",
                    cancel: "Оставить черновик",
                },
            });
        }
        if (approvingServiceWorkLogistTaskIds && approvingServiceWorkLogistTaskIds.length > 0) {
            return notificationModal({
                modal: props.modal,
                setLoading: props.setLoading,
                action: props.action,
                text: approvingServiceWorkText(),
            });
        }
        return props.action();
    });
};

/** Describes the truck and trailer empty-mileage collisions in either bid confirmation flow. */
const emptyMileageCollisionText = ({
    carOption,
    trailerOption,
    collisionEmptyMileageBidId,
    trailerCollisionEmptyMileageBidId,
}: {
    carOption: { number: string };
    trailerOption?: { number?: string } | null;
    collisionEmptyMileageBidId?: number | null;
    trailerCollisionEmptyMileageBidId?: number | null;
}) => (
    <>
        {collisionEmptyMileageBidId && (
            <div>
                Машина <strong>{carOption.number}</strong> выполняет порожнюю заявку&nbsp;
                <a href={paths.bidShow("bid", collisionEmptyMileageBidId)} target="_blank" rel="noreferrer">
                    {collisionEmptyMileageBidId}
                </a>
                .
            </div>
        )}
        {trailerCollisionEmptyMileageBidId && (
            <div>
                Прицеп {trailerOption?.number && <strong>{trailerOption.number} </strong>}используется в порожней
                заявке&nbsp;
                <a href={paths.bidShow("bid", trailerCollisionEmptyMileageBidId)} target="_blank" rel="noreferrer">
                    {trailerCollisionEmptyMileageBidId}
                </a>
                .
            </div>
        )}
    </>
);

const notificationModal = ({
    modal,
    setLoading,
    ...props
}: {
    modal: (props: ModalBodyProps) => void;
    setLoading: (value: boolean) => void;
    title?: string;
    action: () => void;
    text: ReactNode;
    buttons?: { confirm: string; cancel: string };
}) => {
    const config = {
        name: "confirm",
        title: "Запуск заявки в работу",
        onCloseAction: () => setLoading(false),
        buttons: {
            confirm: "Запустить в работу",
            cancel: "Оставить черновик",
        },
        ...props,
    };
    return modal(config);
};

const checkPlanningCondition = (carId: number) => {
    const params = {
        preparedFilter: {
            status: { in: [bidStatusesEnum.Started] },
            car: { id: carId },
            calculationStatus: null,
            lastBidPoint: {
                planEnterDateUtc: {
                    le: new Date(moment().subtract(1, "days").utc().format()),
                },
            },
        },
        length: 1,
    };
    return closeBidsList(params).then((activeBids) => activeBids && activeBids.length && activeBids[0]);
};

export const cargosWithTemperature = (cargos: CargoModel[]) => {
    return cargos.map((cargo, i) => {
        const { name, weight } = cargo;
        if (!name && !weight) {
            return null;
        }
        return (
            <div key={i}>
                {name && <div>{name}</div>}
                {weight && <div>Вес: {weight}т</div>}
            </div>
        );
    });
};

export const surnameAndInitials = (fullName?: string) => {
    const arr = (fullName || "").split(" ");
    return `${arr[0]} ${arr[1] ? `${arr[1][0]}.` : ""} ${arr[2] ? `${arr[2][0]}.` : ""}`;
};

export const getDisplayNameItemChanges = (
    itemChange: BidItemChangeViewModel & { prevData: BidItemChangeViewModel },
) => {
    const { displayName, itemType, prevData } = itemChange;
    const isDriver = [bidItemTypesEnum.Driver, bidItemTypesEnum.SecondaryDriver].includes(itemType);
    const currDisplayName = isDriver ? surnameAndInitials(displayName) : displayName;
    const prevDisplayName = prevData && (isDriver ? surnameAndInitials(prevData.displayName) : prevData.displayName);
    return prevData ? `${prevDisplayName || ""} ⟶ ${currDisplayName}` : currDisplayName;
};

const checkCompoundCondition = ({
    firstPoint,
    lastPoint,
    carId,
    id,
    isEmptyMileageBid,
}: {
    firstPoint: BidPointEditModel;
    lastPoint: BidPointEditModel;
    carId: number;
    id: number;
    isEmptyMileageBid: boolean;
}) => {
    const formattedDate = (date: string) => new Date(moment(date).utc().format());
    const { planEnterDate, planEnterDateOffset } = lastPoint;
    const startDate = formattedDate(
        `${plainDate(firstPoint.planEnterDate)}${timeOffset(firstPoint.planEnterDateOffset)}`,
    );
    const endDate = formattedDate(`${plainDate(planEnterDate)}${timeOffset(planEnterDateOffset)}`);
    const params = {
        id,
        carId,
        firstPointPlanEnterDate: moment(startDate).format("YYYY-MM-DD HH:mm:ss Z"),
        lastPointPlanEnterDate: moment(endDate).format("YYYY-MM-DD HH:mm:ss Z"),
        isEmptyMileageBid: !!isEmptyMileageBid,
    };
    return getBidCollisions(params).then((data) => ({
        hasCompoundBids: data.compoundBidWillBeCreated,
        collisionEmptyMileageBidId: data.collisionEmptyMileageBidId,
        trailerCollisionEmptyMileageBidId: data.trailerCollisionEmptyMileageBidId,
        approvingServiceWorkLogistTaskIds: data.approvingServiceWorkLogistTaskIds,
    }));
};

export const itemChangeTitle = (
    shiftCar: CarNumberModel,
    shiftDriver: DriverUserSimpleModel,
    shiftSecondaryDriver: DriverUserSimpleModel,
    shiftTrailer: TrailerNumberModel,
    options = { interactive: true },
) => {
    return (
        <>
            {shiftDriver && (
                <div>
                    Водитель -&nbsp;
                    {options.interactive ? (
                        <a href={paths.driversShow(shiftDriver.id)} target="_blank" rel="noopener noreferrer">
                            {shiftDriver.shortName}
                        </a>
                    ) : (
                        <strong>{shiftDriver.shortName}</strong>
                    )}
                </div>
            )}
            {shiftSecondaryDriver && (
                <div>
                    Второй водитель -&nbsp;
                    {options.interactive ? (
                        <a href={paths.driversShow(shiftSecondaryDriver.id)} target="_blank" rel="noopener noreferrer">
                            {shiftSecondaryDriver.shortName}
                        </a>
                    ) : (
                        <strong>{shiftSecondaryDriver.shortName}</strong>
                    )}
                </div>
            )}
            {shiftCar && (
                <div>
                    Машина -&nbsp;
                    {options.interactive ? (
                        <a href={paths.trucksShow(shiftCar.id)} target="_blank" rel="noopener noreferrer">
                            {shiftCar.number}
                        </a>
                    ) : (
                        <strong>{shiftCar.number}</strong>
                    )}
                </div>
            )}
            {shiftTrailer && (
                <div>
                    Прицеп -&nbsp;
                    {options.interactive ? (
                        <a href={paths.trailersShow(shiftTrailer.id)} target="_blank" rel="noopener noreferrer">
                            {shiftTrailer.number}
                        </a>
                    ) : (
                        <strong>{shiftTrailer.number}</strong>
                    )}
                </div>
            )}
        </>
    );
};

export const parallelItemChangeModalTitle = (
    car: CarNumberModel,
    driver: DriverUserSimpleModel,
    trailer: TrailerNumberModel,
) => {
    const items = [];
    if (car) {
        items.push("машину");
    }
    if (driver) {
        items.push("водителя");
    }
    if (trailer) {
        items.push("прицеп");
    }
    return `На ${items.join(", ")} запланирована перецепка в другой заявке`;
};

export const comparePointsWithSegments = (
    bidPoints: BidPointViewModel[],
    route: { segments: SegmentItemModel[] },
    ignoreStartPoint: boolean = false,
) => {
    const { segments } = route || {};

    if (!segments) {
        return true;
    }

    let segmentsPointsIds: (number | undefined)[] = [];
    const pointTypes = [
        mapObjectTypesEnum.BidPoint,
        mapObjectTypesEnum.ServicePoint,
        mapObjectTypesEnum.CounterpartyPoint,
    ];
    if (!ignoreStartPoint) {
        pointTypes.push(mapObjectTypesEnum.StartBidPoint);
    }
    if (segments) {
        const pointIds = segments.reduce(
            (prev, { mapObjectFrom, mapObjectTo }) => {
                if (mapObjectFrom && pointTypes.includes(mapObjectFrom.type)) {
                    prev.push(mapObjectFrom.id ? +mapObjectFrom.id : undefined);
                }
                if (mapObjectTo && pointTypes.includes(mapObjectTo.type)) {
                    prev.push(mapObjectTo.id ? +mapObjectTo.id : undefined);
                }
                return prev;
            },
            [] as (number | undefined)[],
        );
        segmentsPointsIds = uniq(pointIds);
    }
    const bidPointsIds = (ignoreStartPoint ? withoutStartPoint(bidPoints) : bidPoints).map((x: BidPointViewModel) =>
        x.geozone ? x.geozone.id : undefined,
    );

    return !arraysEqual(segmentsPointsIds, bidPointsIds);
};
